import React, { useCallback, useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { CalendarIcon, MapPinIcon, TruckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { travelAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isStaff } from '../types/auth';
import { EventType, getEventTypeLabel } from '../types/event';
import type { EventTravel, TravelOverview } from '../types/travel';
import { formatTravelTime } from '../types/travel';
import TravelPlanView from '../components/travel/TravelPlanView';
import TravelEditorModal from '../components/travel/TravelEditorModal';
import TravelRespondPanel from '../components/travel/TravelRespondPanel';
import VehiclesTab from '../components/travel/VehiclesTab';
import EmptyState from '../components/ui/EmptyState';
import { getErrorMessage } from '../utils/errorMessage';

type Tab = 'trips' | 'vehicles';

const assignmentText = (a: TravelOverview['myAssignments'][number]) => {
  if (a.goesDirectly) return 'egyenesen a helyszínre megy';
  if (!a.vehicleName) return a.needsRide ? 'fuvarra vár' : 'még nincs beosztva';
  const stop = a.stopName ? `, ${a.stopName}` : '';
  const time = a.meetTime ? `, ${formatTravelTime(a.meetTime)}-ra` : '';
  return `${a.vehicleName}${stop}${time}`;
};

/**
 * Utazások: a közelgő események utazásszervezése. A stáb innen indítja a
 * szervezést és kezeli a klub járműveit; a játékos és a szülő azt látja, ő
 * (vagy a gyermeke) melyik járművel, honnan, mikor indul.
 */
const Travel: React.FC = () => {
  usePageTitle('Utazások');
  const { user } = useAuth();
  const { showToast } = useToast();
  const staff = isStaff(user);

  const [tab, setTab] = useState<Tab>('trips');
  const [trips, setTrips] = useState<TravelOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlyPlanned, setOnlyPlanned] = useState(false);
  const [typeFilter, setTypeFilter] = useState<EventType | 'all'>('all');
  const [viewing, setViewing] = useState<EventTravel | null>(null);
  const [editing, setEditing] = useState<EventTravel | null>(null);
  const [editorKey, setEditorKey] = useState(0);

  const load = useCallback(async () => {
    try {
      setTrips(await travelAPI.getUpcoming());
    } catch (err) {
      console.error('Az utazások betöltése nem sikerült:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (trip: TravelOverview) => {
    try {
      const travel = await travelAPI.getTravel(trip.eventId);
      // A stáb az új utazást rögtön szervezi; a szülő a nézetben jelez
      if (travel.exists || !travel.canEdit) setViewing(travel);
      else setEditing(travel);
    } catch (err: any) {
      showToast(getErrorMessage(err, 'Az utazás betöltése nem sikerült.'), 'error');
    }
  };

  const visibleTrips = trips.filter(
    (t) => (!onlyPlanned || t.hasTravel) && (typeFilter === 'all' || t.eventType === typeFilter)
  );
  // Csak azok a típusok kapnak szűrőt, amelyekből van esemény
  const presentTypes = Array.from(new Set(trips.map((t) => t.eventType))).sort((a, b) => a - b);

  const tripsTab = (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        {presentTypes.length > 1 && (
          <div className="flex gap-1 overflow-x-auto">
            {(['all', ...presentTypes] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setTypeFilter(type)}
                aria-pressed={typeFilter === type}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium ${
                  typeFilter === type ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
                }`}
              >
                {type === 'all' ? 'Mind' : getEventTypeLabel(type)}
              </button>
            ))}
          </div>
        )}
        {staff && (
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={onlyPlanned}
              onChange={(e) => setOnlyPlanned(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Csak a megszervezett utazások
          </label>
        )}
      </div>

      {loading ? (
        <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">Betöltés...</div>
      ) : visibleTrips.length === 0 ? (
        <div className="rounded-lg bg-white shadow-sm">
          <EmptyState
            icon={TruckIcon}
            title="Nincs közelgő utazás"
            description={
              staff
                ? 'A következő 60 napban nincs olyan esemény, amihez utazást lehetne szervezni.'
                : 'Ha egy eseményhez megszervezik az utazást, itt látod, ki melyik járművel, honnan indul.'
            }
          />
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {visibleTrips.map((trip) => {
            const free = trip.totalSeats - trip.seatedCount;
            return (
              <li key={trip.eventId} className="min-w-0">
                <button
                  type="button"
                  onClick={() => open(trip)}
                  className="w-full rounded-lg bg-white p-4 text-left shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900">{trip.eventTitle}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 shrink-0" />
                        {formatTravelTime(trip.eventStart, true)}
                        <span className="text-gray-400">·</span>
                        {getEventTypeLabel(trip.eventType)}
                        {trip.ageGroup && (
                          <>
                            <span className="text-gray-400">·</span>
                            {trip.ageGroup}
                          </>
                        )}
                      </p>
                      {trip.destination && (
                        <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-gray-500">
                          <MapPinIcon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{trip.destination}</span>
                        </p>
                      )}
                    </div>
                    {trip.hasTravel && trip.vehicleCount > 0 ? (
                      <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Megszervezve
                      </span>
                    ) : trip.hasTravel ? (
                      // Van utazás, de még nincs jármű: ez még szervezés alatt áll
                      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Szervezés alatt
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        Nincs szervezve
                      </span>
                    )}
                  </div>

                  {trip.hasTravel && (
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                      <span>{trip.vehicleCount} jármű</span>
                      <span>
                        {trip.seatedCount} / {trip.totalSeats} hely foglalt
                        {free > 0 && <span className="text-gray-400"> ({free} szabad)</span>}
                      </span>
                      {trip.directCount > 0 && <span>{trip.directCount} a helyszínre megy</span>}
                      {(trip.needsRideCount ?? 0) > 0 && (
                        <span className="text-amber-700">{trip.needsRideCount} fuvarra vár</span>
                      )}
                      {staff && trip.unassignedExtraCount > 0 && (
                        <span className="text-amber-700">{trip.unassignedExtraCount} beosztásra vár</span>
                      )}
                    </div>
                  )}

                  {trip.myAssignments.length > 0 && (
                    <div className="mt-3 space-y-0.5 rounded-md bg-primary-50 p-2 text-sm text-primary-900">
                      {trip.myAssignments.map((a) => (
                        <p key={a.name}>
                          <span className="font-semibold">{a.name}:</span> {assignmentText(a)}
                        </p>
                      ))}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Utazások</h1>
        <p className="text-sm text-gray-500">
          {staff
            ? 'Ki melyik járművel, honnan, mikor indul - mérkőzésekre, tornákra.'
            : 'Hogyan jutsz el a közelgő eseményekre: melyik járművel, honnan, mikor.'}
        </p>
      </div>

      {staff && (
        <div className="mb-4 flex gap-1 border-b border-gray-200" role="tablist">
          {([
            ['trips', 'Közelgő utazások'],
            ['vehicles', 'Járművek'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {staff && tab === 'vehicles' ? <VehiclesTab /> : tripsTab}

      {viewing && (
        <Dialog open onClose={() => setViewing(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
            <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-h-[90vh] md:max-w-3xl md:rounded-lg">
              <div className="flex shrink-0 items-start justify-between gap-2 border-b p-4 md:px-6">
                <div className="min-w-0">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">{viewing.eventTitle}</Dialog.Title>
                  <p className="text-sm text-gray-500">{formatTravelTime(viewing.eventStart, true)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {viewing.canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(viewing);
                        setViewing(null);
                      }}
                      className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                    >
                      Szerkesztés
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setViewing(null)}
                    aria-label="Bezárás"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-4 pb-safe-4 md:px-6">
                {viewing.canRespond && (
                  <TravelRespondPanel
                    travel={viewing}
                    onChange={(updated) => {
                      setViewing(updated);
                      load();
                    }}
                  />
                )}
                {viewing.exists && (
                  <TravelPlanView travel={viewing} showUnassigned={viewing.canEdit} hideMine={viewing.canRespond} />
                )}
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}

      {editing && (
        <TravelEditorModal
          key={editorKey}
          travel={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setViewing(saved);
            load();
          }}
          onDeleted={() => {
            setEditing(null);
            load();
          }}
          onReload={async () => {
            try {
              setEditing(await travelAPI.getTravel(editing.eventId));
              setEditorKey((k) => k + 1);
            } catch (err: any) {
              showToast(getErrorMessage(err, 'Az utazás betöltése nem sikerült.'), 'error');
            }
          }}
        />
      )}
    </div>
  );
};

export default Travel;
